export type EnvironmentName = "development" | "stage" | "prod" | "preprod";

export function getGoogleTagManagerId(envName: string): string {
  return ["prod", "stage"].includes(envName) ? "GTM-NTW7353" : "GTM-T8FRD4Q9";
}

/**
 * VWO FME (Feature Management & Experimentation) configuration for Aspen Dental.
 * Each environment has its own SDK key so feature flag rules are isolated.
 */
const VWO_CONFIG = {
  accountId: "613301",
  sdkKeys: {
    development: "6c8816624ca9ffa3c30ffceb4b4016ad",
    stage: "f2b34a73d0a1f45999e975d2028780ef",
    prod: "1470d5ad10c9099f145476dd908b0c78",
  },
} as const;

export function getVwoSdkKey(envName: string): string {
  if (["prod", "preprod"].includes(envName)) return VWO_CONFIG.sdkKeys.prod;
  if (envName === "stage") return VWO_CONFIG.sdkKeys.stage;
  return VWO_CONFIG.sdkKeys.development;
}

export type Config = {
  siteName: string;
  brand_name: string;
  googleTagManagerId: string;
  vwoAccountId: string;
  vwoSdkKey: string;
  baseUrl: {
    development: string;
    stage: string;
    prod: string;
    preprod: string;
  };
  services: {
    prod: {
      DENTRINO_API_URL: string;
      DENTRINO_CLIENT_ID: string;
      DENTRINO_CLIENT_SECRET: string;
      FORM_EVENTS_URL: string;
    };
    nonprod: {
      DENTRINO_API_URL: string;
      DENTRINO_CLIENT_ID: string;
      DENTRINO_CLIENT_SECRET: string;
      FORM_EVENTS_URL: string;
    };
  };
  odpPath: string;
  contentfulAppId: string;
  siteURL: string;
  schedulePath: string;
  blogPath: string;
  name: string;
  logo: {
    dark: string;
    light: string;
  };
};

const config: Config = {
  siteName: "Aspen Dental Marketing",
  brand_name: "ASPEN_DENTAL",
  googleTagManagerId: getGoogleTagManagerId(process.env.ENVIRONMENT_NAME ?? "development"),
  vwoAccountId: VWO_CONFIG.accountId,
  vwoSdkKey: getVwoSdkKey(process.env.ENVIRONMENT_NAME ?? ""),
  baseUrl: {
    development: "http://localhost:3000",
    stage: "https://wwwstg.aspendental.com/bb",
    prod: "https://www.aspendental.com/bb",
    preprod: "https://preprod.aspendental.com/bb",
  },
  services: {
    prod: {
      DENTRINO_API_URL: "https://api.dentrino.ai/public-api/simulations/cosmetic",
      DENTRINO_CLIENT_ID: "ODI3MjExODIxNjA2OWRSeXExYmQw",
      DENTRINO_CLIENT_SECRET: "f9d6bf94823ddbb4cc75e0f28f2a8a8c8811c8dd849051a3307d236aeb61b653",
      FORM_EVENTS_URL: "https://api.teamtag.com/form/events",
    },
    nonprod: {
      DENTRINO_API_URL: "https://api.e91efc7.dentrino.ai/public-api/simulations/cosmetic",
      DENTRINO_CLIENT_ID: "ODI3NDYxNDY5NTU1MC4lUllAQWlS",
      DENTRINO_CLIENT_SECRET: "736dcd2947189e6878baea292d62271900a46f5695ac2034ba9072606a7cc2ff",
      FORM_EVENTS_URL: "https://aspen-userevents.gcp.stage.aspendental.com/form/events",
    },
  },
  odpPath: "dentist",
  contentfulAppId: "AspenDental - Office Details",
  logo: {
    dark: "https://images.ctfassets.net/m8zwsu9tyucg/7jzYqWlL2Z1NyUWPgR7mpc/922ffe5db0d5cd28bcb42653e7d9a5e6/AspenLogoDark.svg",
    light:
      "https://images.ctfassets.net/m8zwsu9tyucg/1OLI6iOYAFp0Mvqm46Yyor/8908782b135cb2d577fe727a536434b1/logo-aspendental-white.svg",
  },
  siteURL: "https://www.aspendental.com",
  name: "Aspen Dental",
  schedulePath: "schedule-an-appointment",
  blogPath: "dental-care-resources",
};

export default config;
